import type { MuxClient, Session, SessionStatus } from "@mux/sdk/v2/client"
import { batch, createMemo, type Owner } from "solid-js"
import { reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { isCanonicalOracleSession } from "@/context/oracle/session-cache"
import { getPinnedSessionIds } from "@/utils/pinned-sessions"
import { getActiveGridSessionIDs } from "@/pages/grid/grid-active-sessions"
import type { serverFetch } from "@/transport/server"
import { getSessionsByID, getSidebarProjection } from "@/transport/session"
import { isAbortError } from "@/utils/server-errors"
import { workspaceMatches } from "@/utils/workspace"
import { createPinnedSessionLoader } from "./pinned-session-loader"
import { fetchInitialRootSessionPages, fetchRemainingRootSessionPages, fetchRootSessionPage, mergeSessionPage } from "./session-page"
import { createSidebarSessionStore } from "./sidebar-session-store"
import { sessionSnoozedUntil } from "./session-snooze"
import { newestSessions } from "./session-revision"
import { trimSessions } from "./session-trim"
import type { GlobalStore, State } from "./types"
import { getActiveSessionIds, sortSessionsByID } from "./utils"

type ChildStore = [Store<State>, SetStoreFunction<State>]

export function createSidebarController(input: {
  owner: Owner
  client: MuxClient
  serverKey: () => string
  connection: () => Parameters<typeof serverFetch>[0] | undefined
  store: Store<GlobalStore>
  setStore: SetStoreFunction<GlobalStore>
  /** A mounted surface shows this session, so a list refresh must keep it. */
  retained: (directory: string, sessionID: string) => boolean
  pin: (directory: string) => void
  unpin: (directory: string) => void
  child: (directory: string) => ChildStore
  overlayStatuses: (statuses: Record<string, SessionStatus>) => Record<string, SessionStatus>
  refreshProjectionWith: <T>(
    load: () => Promise<T>,
    statuses: (value: T) => Record<string, SessionStatus>,
    apply: (value: T, statuses: Record<string, SessionStatus>) => void,
  ) => () => Promise<boolean>
}) {
  const loads = new Map<string, Promise<void>>()
  const controllers = new Map<string, AbortController>()
  const loaded = new Set<string>()
  // Sessions a list refresh keeps although the fetched page may omit them: the
  // routed chat, Grid windows not yet mounted, and every session a mounted
  // surface shows, including hidden ones the server never lists (a Grid Oracle
  // window, a comparison branch). Resolved at commit time: hydration and
  // navigation can both happen while the request is in flight.
  const keptSessionIDs = (directory: string) => {
    const ids = getActiveSessionIds(
      typeof location === "undefined" ? "" : location.pathname,
      "",
      getActiveGridSessionIDs(),
      input.store.session,
    )
    for (const session of input.store.session) {
      // Surfaces retain by workspace ID; a list may be loaded by worktree path.
      if (input.retained(session.workspaceID || directory, session.id)) ids.add(session.id)
    }
    return ids
  }
  const pinnedSessions = createPinnedSessionLoader({
    getMany: (sessionIDs) => {
      const connection = input.connection()
      if (!connection) return Promise.resolve([])
      return getSessionsByID(connection, sessionIDs)
    },
  })
  const sessionsByWorkspace = createMemo(() => {
    const index = new Map<string, Session[]>()
    for (const session of input.store.session) {
      const workspaceID = session.workspaceID || session.directory
      if (!workspaceID) continue
      const sessions = index.get(workspaceID)
      if (sessions) sessions.push(session)
      else index.set(workspaceID, [session])
    }
    return index
  })
  const store = createSidebarSessionStore({
    owner: input.owner,
    serverKey: input.serverKey,
    sessionsForWorkspace: (directory) => sessionsByWorkspace().get(directory) ?? [],
    pinnedSessionIds: () => getPinnedSessionIds(input.serverKey()),
    sessionTotal: (directory) => input.store.sessionTotal[directory],
    replenish,
    fetchArchive: async (directory) => {
      const result = await input.client.session.list({ directory, roots: true, archived: "only", limit: 100 })
      return (result.data ?? [])
        .filter((session) => !!session?.id && !session.parentID && workspaceMatches(session, directory))
        .sort((a, b) =>
          (b.time?.archived ?? 0) - (a.time?.archived ?? 0) ||
          (b.time?.updated ?? 0) - (a.time?.updated ?? 0))
    },
    mergeArchived: (_directory, incoming) => {
      const sessions = new Map(input.store.session.map((session) => [session.id, session]))
      for (const session of newestSessions(input.store, incoming)) sessions.set(session.id, session)
      input.setStore("session", reconcile(sortSessionsByID([...sessions.values()]), { key: "id" }))
    },
  })
  const projectionController = new AbortController()
  const refreshProjection = input.refreshProjectionWith(
    async () => {
      const connection = input.connection()
      if (!connection) throw new Error("No active server")
      const knownSnoozed = input.store.session.filter((session) => sessionSnoozedUntil(session) > 0).map((session) => session.id)
      const projection = await getSidebarProjection(connection, projectionController.signal)
      const current = new Set(projection.sessions.map((session) => session.id))
      const missing = knownSnoozed.filter((id) => !current.has(id))
      // A reminder can expire, be archived, or be deleted while SSE is offline.
      const resolved = await getSessionsByID(connection, missing, projectionController.signal)
      const found = new Set(resolved.map((session) => session.id))
      return { ...projection, sessions: [...projection.sessions, ...resolved], removedSnoozedIDs: missing.filter((id) => !found.has(id)) }
    },
    (projection) => projection.statuses ?? {},
    (projection, statuses) => {
      const current = new Map(input.store.session.map((session) => [session.id, session]))
      for (const id of projection.removedSnoozedIDs) current.delete(id)
      for (const session of newestSessions(input.store, projection.sessions ?? [])) current.set(session.id, session)
      batch(() => {
        input.setStore("session", reconcile(sortSessionsByID([...current.values()]), { key: "id" }))
        input.setStore("session_status", reconcile(input.overlayStatuses(statuses)))
      })
    },
  )

  async function load(directory: string, options?: { more?: boolean; force?: boolean }): Promise<void> {
    const serverKey = input.serverKey()
    const connection = input.connection()
    if (!serverKey || !connection) return
    const pending = loads.get(directory)
    if (pending) {
      await pending
      // A forced load must reflect the server after this call, not a request already in flight.
      if (options?.more || options?.force) return load(directory, options)
      return
    }

    input.pin(directory)
    const [_child, setChild] = input.child(directory)
    const page = store.page(directory)
    if ((!options?.force && !options?.more && loaded.has(directory)) ||
      (options?.more && page.pageStatus !== "ready" && page.pageStatus !== "error") ||
      (options?.more && !page.nextCursor)) {
      input.unpin(directory)
      return
    }
    // Refreshing a loaded list keeps its rows and pagination visible until the replacement lands.
    const refreshing = options?.force === true && page.pageStatus === "ready"
    const controller = new AbortController()
    controllers.set(directory, controller)
    if (!refreshing) store.setPage(directory, { pageStatus: "loading", loadingMore: options?.more === true })
    const request = options?.more
      ? fetchRemainingRootSessionPages({ server: connection, directory, limit: page.limit, cursor: page.nextCursor!, signal: controller.signal })
      : fetchInitialRootSessionPages({ server: connection, directory, limit: page.limit, pinnedIds: getPinnedSessionIds(serverKey), signal: controller.signal })
    const promise = request.then(async (result) => {
      if (input.serverKey() !== serverKey) return
      const missingPinnedIDs = getPinnedSessionIds(serverKey)
        .filter((id) => !result.items.some((session) => session.id === id))
      const pinned = await pinnedSessions.load(serverKey, missingPinnedIDs)
      return { ...result, items: [...result.items, ...pinned.filter((session): session is Session => !!session)] }
    }).then((result) => {
      if (!result || input.serverKey() !== serverKey) return
      const roots = newestSessions(input.store, result.items.filter((session) => !!session?.id && workspaceMatches(session, directory) && !session.time?.archived))
      const activeIDs = keptSessionIDs(directory)
      // The list never returns hidden chats, so the workspace's Oracle, which
      // backs its row and working indicator, is kept from the store.
      const existing = input.store.session.filter((session) =>
        workspaceMatches(session, directory) && !session.time?.archived &&
        (options?.more || activeIDs.has(session.id) || isCanonicalOracleSession(session, session.workspaceID)),
      )
      const sessions = trimSessions(mergeSessionPage(existing, roots), {
        limit: Number.POSITIVE_INFINITY,
        permission: input.store.permission,
        pinned: new Set(getPinnedSessionIds(serverKey)),
        activeSessionIds: activeIDs,
      })
      batch(() => {
        input.setStore("session", reconcile(sortSessionsByID([
          ...input.store.session.filter((session) => !workspaceMatches(session, directory) || (!!session.time?.archived && result.hasArchived !== false)),
          ...sessions,
        ]), { key: "id" }))
        store.replace(directory, sessions, result.nextCursor !== undefined, result.hasArchived)
        store.setPage(directory, { nextCursor: result.nextCursor, pageStatus: "ready", loadingMore: false })
        setChild("status", "complete")
      })
      loaded.add(directory)
    }).catch((error) => {
      if (isAbortError(error)) return
      if (input.serverKey() !== serverKey) return
      if (!refreshing) store.setPage(directory, { pageStatus: "error", loadingMore: false })
      throw error
    })
    loads.set(directory, promise)
    const cleanup = () => {
      loads.delete(directory)
      controllers.delete(directory)
      input.unpin(directory)
    }
    void promise.then(cleanup, cleanup)
    return promise
  }

  async function replenish(directory: string): Promise<void> {
    const pending = loads.get(directory)
    if (pending) {
      await pending
      return replenish(directory)
    }

    const serverKey = input.serverKey()
    const connection = input.connection()
    const page = store.page(directory)
    if (!serverKey || !connection || !page.nextCursor || !store.needsReplenish(directory)) return

    input.pin(directory)
    const controller = new AbortController()
    controllers.set(directory, controller)
    const promise = fetchRootSessionPage({
      server: connection,
      directory,
      limit: 1,
      cursor: page.nextCursor,
      signal: controller.signal,
    }).then((result) => {
      if (input.serverKey() !== serverKey) return
      const current = input.store.session.filter(
        (session) => workspaceMatches(session, directory) && !session.time?.archived,
      )
      const incoming = newestSessions(input.store, result.items.filter(
        (session) => !!session?.id && workspaceMatches(session, directory) && !session.time?.archived,
      ))
      const sessions = trimSessions(mergeSessionPage(current, incoming), {
        limit: Number.POSITIVE_INFINITY,
        permission: input.store.permission,
        pinned: new Set(getPinnedSessionIds(serverKey)),
        activeSessionIds: keptSessionIDs(directory),
      })
      batch(() => {
        input.setStore("session", reconcile(sortSessionsByID([
          ...input.store.session.filter((session) => !workspaceMatches(session, directory) || (!!session.time?.archived && result.hasArchived !== false)),
          ...sessions,
        ]), { key: "id" }))
        store.replace(directory, sessions, result.nextCursor !== undefined, result.hasArchived)
        store.setPage(directory, { nextCursor: result.nextCursor })
      })
    }).catch((error) => {
      if (isAbortError(error) || input.serverKey() !== serverKey) return
      throw error
    })
    loads.set(directory, promise)
    const cleanup = () => {
      loads.delete(directory)
      controllers.delete(directory)
      input.unpin(directory)
    }
    void promise.then(cleanup, cleanup)
    return promise
  }

  // Loaded lists only learn about chats created elsewhere through session events,
  // so a refresh refetches them along with the active/snoozed projection.
  async function refresh(): Promise<void> {
    await Promise.all([
      refreshProjection(),
      ...[...loaded].map((directory) => load(directory, { force: true })),
    ])
  }

  store.bindLoader(load)
  return {
    store,
    load,
    refreshProjection,
    refresh,
    isLoading: (directory: string) => loads.has(directory),
    disposeDirectory(directory: string) {
      controllers.get(directory)?.abort()
      controllers.delete(directory)
      loaded.delete(directory)
      store.dispose(directory)
    },
    dispose() {
      projectionController.abort()
      for (const controller of controllers.values()) controller.abort()
      controllers.clear()
      loads.clear()
      loaded.clear()
      pinnedSessions.clear()
      store.disposeAll()
    },
  }
}
